import { type RefObject, useCallback, useEffect, useState } from "react";

interface UseDesktopImageGalleryOptions {
  allImagesCount: number;
  currentProductId: string | null;
  desktopImagesRef: RefObject<HTMLDivElement | null>;
  imagesSentinelRef: RefObject<HTMLDivElement | null>;
}

interface UseDesktopImageGalleryResult {
  visibleImageIndex: number;
  showImageCounter: boolean;
  resetGallery: () => void;
}

export function useDesktopImageGallery({
  allImagesCount,
  currentProductId,
  desktopImagesRef,
  imagesSentinelRef,
}: UseDesktopImageGalleryOptions): UseDesktopImageGalleryResult {
  const [visibleImageIndex, setVisibleImageIndex] = useState(0);
  const [showImageCounter, setShowImageCounter] = useState(true);

  const resetGallery = useCallback(() => {
    setVisibleImageIndex(0);
    setShowImageCounter(true);
  }, []);

  // Track which secondary image is visible in the scrolling column via IntersectionObserver.
  // Only observes the secondary images (not the sticky face image) so the counter
  // defaults to "1/N" when no secondary image is >=50% visible.
  useEffect(() => {
    const visibleIndices = new Set<number>();
    if (allImagesCount <= 1) return;

    let observer: IntersectionObserver | null = null;
    const frameId = window.requestAnimationFrame(() => {
      const container = desktopImagesRef.current;
      if (!container) return;
      const images = container.querySelectorAll("[data-secondary-image-index]");
      if (images.length === 0) return;

      observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            const idx = parseInt(
              entry.target.getAttribute("data-secondary-image-index") || "0",
              10
            );
            if (entry.isIntersecting) {
              visibleIndices.add(idx);
            } else {
              visibleIndices.delete(idx);
            }
          }
          if (visibleIndices.size > 0) {
            setVisibleImageIndex(Math.min(...visibleIndices));
          } else {
            setVisibleImageIndex(0);
          }
        },
        { threshold: 0.5 }
      );

      images.forEach((img) => observer?.observe(img));
    });

    return () => {
      window.cancelAnimationFrame(frameId);
      observer?.disconnect();
    };
  }, [allImagesCount, currentProductId, desktopImagesRef]);

  // Hide image counter once the sentinel at the end of images enters the viewport.
  useEffect(() => {
    const sentinel = imagesSentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setShowImageCounter(!entry.isIntersecting);
      },
      { threshold: 0 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [currentProductId, allImagesCount, imagesSentinelRef]);

  return { visibleImageIndex, showImageCounter, resetGallery };
}
